package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.tedd.db.Asset
import com.tedd.db.Asset_valuation
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.TODAY_ISO
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis

/**
 * Biens : ce qui a de la valeur sans être un compte. On ne les débite pas, on constate
 * leur valeur, et chaque constat laisse une ligne datée.
 */
class AssetRepository(private val db: TeddDatabase) {

    fun observeAll(): Flow<List<Asset>> =
        db.assetQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun observeValuations(): Flow<List<Asset_valuation>> =
        db.assetValuationQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    suspend fun create(
        name: String,
        kind: String,
        currency: String = "XOF",
        value: Long? = null,
        purchasePrice: Long? = null,
        acquiredDate: String? = null,
        note: String? = null,
        orderIndex: Long = 0,
        id: String = newId("as"),
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        val valuedAt = if (value != null) TODAY_ISO else null
        db.assetQueries.insert(
            id, name.trim(), kind, currency, value, purchasePrice, acquiredDate,
            valuedAt, note?.takeIf { it.isNotBlank() }, 0L, orderIndex, now, now,
        )
        // Le premier constat entre aussi dans l'historique.
        if (value != null) recordValuation(id, value, TODAY_ISO, null)
        id
    }

    suspend fun update(
        id: String,
        name: String,
        kind: String,
        currency: String,
        value: Long?,
        purchasePrice: Long?,
        acquiredDate: String?,
        valuedAt: String?,
        note: String?,
        archived: Boolean,
        orderIndex: Long,
    ) = withContext(Dispatchers.Default) {
        db.assetQueries.update(
            name.trim(), kind, currency, value, purchasePrice, acquiredDate, valuedAt,
            note?.takeIf { it.isNotBlank() }, if (archived) 1L else 0L, orderIndex, nowMillis(), id,
        )
    }

    /** Constate une valeur à une date : met à jour le bien et ajoute une ligne d'historique. */
    suspend fun recordValuation(assetId: String, value: Long, date: String = TODAY_ISO, note: String? = null) =
        withContext(Dispatchers.Default) {
            val now = nowMillis()
            db.assetValuationQueries.insert(newId("av"), assetId, date, value, note?.takeIf { it.isNotBlank() }, now, now)
            db.assetQueries.updateValue(value, date, now, assetId)
        }

    suspend fun softDelete(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.assetQueries.softDelete(now, now, id)
    }
}
